import RenderEntity, { RENDER_ENTITY_FORMAT3, RENDER_ENTITY_FORMAT4 } from 'ui-component/renders/RenderEntity';
import { lazy } from 'react';

// material-ui
import { Stack, Typography, Tooltip, Box } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';
import { FormattedMessage } from 'react-intl';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import RenderWorkflowState, {
    RENDER_WORKFLOW_STATE_FORMAT2,
    RENDER_WORKFLOW_STATE_FORMAT4,
    RENDER_WORKFLOW_STATE_FORMAT5
} from 'ui-component/renders/Workflow/RenderWorkflowState';
import RenderExecSettingsGridCell from 'ui-component/renders/RenderExecSettingsGridCell';

// assets
import FolderTwoToneIcon from '@mui/icons-material/FolderTwoTone';
import PriorityHighTwoToneIcon from '@mui/icons-material/PriorityHighTwoTone';
import DensityMediumTwoToneIcon from '@mui/icons-material/DensityMediumTwoTone';
import LowPriorityTwoToneIcon from '@mui/icons-material/LowPriorityTwoTone';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockSharpIcon from '@mui/icons-material/LockSharp';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import AssignmentTwoToneIcon from '@mui/icons-material/AssignmentTwoTone';
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

// lazy loaded components
const BusinessOpportunityTypeSelect = lazy(() => import('ui-component/fields/EntitySelect/workmgmt/BusinessOpportunityTypeSelect'));
const DepartmentSelect = lazy(() => import('ui-component/fields/EntitySelect/common/DepartmentSelect'));
const ClientSelect = lazy(() => import('ui-component/fields/EntitySelect/crm/ClientSelect'));
const EmployeeSelect = lazy(() => import('ui-component/fields/EntitySelect/common/EmployeeSelect'));
const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));
const BusinessOpportunityStateSelect = lazy(() => import('ui-component/fields/WorkflowSelect/BusinessOpportunityStateSelect'));
const PrioritySelect = lazy(() => import('ui-component/fields/PrioritySelect'));

const formatExpectedValueAsCurrency = (value) =>
    new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'AOA',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(capabilities, params?.row, permissionData, onClickActions);

                if (
                    !params?.row?.deletedAt &&
                    !gridActions?.some((action) => action?.key === `actionedit-row${params?.row?.id}`) &&
                    permissionData?.some((perm) => perm?.startsWith('tasks-'))
                ) {
                    gridActions.push({
                        key: `actionedit-row${params?.row?.id}`,
                        icon: <EditTwoToneIcon fontSize="small" />,
                        label: 'ui-comp-button-edit',
                        position: 2,
                        showInMenu: true,
                        onClick: () => {
                            if (
                                'onClickRowActionEdit' in (onClickActions || {}) &&
                                typeof onClickActions.onClickRowActionEdit === 'function'
                            ) {
                                onClickActions.onClickRowActionEdit(params?.row);
                            }
                        }
                    });
                }

                permissionData?.forEach((perm) => {
                    switch (perm) {
                        case 'canEdit':
                            if (params?.row?.deletedAt) {
                                return;
                            }

                            gridActions.push({
                                key: `actioneditinnewtab-row${params?.row?.id}`,
                                icon: <EditTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-editinnewtab',
                                position: 2.1,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionEditInNewTab' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionEditInNewTab === 'function'
                                    ) {
                                        onClickActions.onClickRowActionEditInNewTab(params?.row);
                                    }
                                }
                            });
                            break;
                        case 'canCreateWorkOrder':
                            if (params?.row?.deletedAt || !['completed', 'awarded']?.includes(params?.row?.state)) {
                                return;
                            }

                            gridActions.push({
                                key: `actioncreateworkorder-row${params?.row?.id}`,
                                icon: <AssignmentTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-create-workorder',
                                position: 7,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionCreateWorkOrder' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionCreateWorkOrder === 'function'
                                    ) {
                                        onClickActions.onClickRowActionCreateWorkOrder(params?.row);
                                    }
                                }
                            });
                            break;
                        default: // do nothing
                    }
                });

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'code',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={params.value}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <FolderTwoToneIcon
                                sx={{
                                    fontSize: '0.875rem',
                                    color:
                                        'r' in (params?.row?.tplBusinessOpportunityColor || {}) &&
                                        'g' in (params?.row?.tplBusinessOpportunityColor || {}) &&
                                        'b' in (params?.row?.tplBusinessOpportunityColor || {}) &&
                                        'a' in (params?.row?.tplBusinessOpportunityColor || {})
                                            ? `rgba(${params.row.tplBusinessOpportunityColor.r}, ${params.row.tplBusinessOpportunityColor.g}, ${params.row.tplBusinessOpportunityColor.b}, ${params.row.tplBusinessOpportunityColor.a})`
                                            : 'secondary'
                                }}
                            />
                            <Typography
                                variant="inherit"
                                color="inherit"
                                sx={{
                                    overflow: 'hidden',
                                    display: 'block',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                    // ':hover': { color: 'primary.main' }
                                    // cursor: 'pointer'
                                }}
                            >
                                {params.value}
                            </Typography>
                        </Stack>
                    </Tooltip>
                ),
            label: 'traits-appentityhasautogencode-code'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Tooltip
                    title={
                        <div
                            style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1…') }}
                        />
                    }
                >
                    <div
                        style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1…') }}
                    />
                </Tooltip>
            ),
            flex: 1,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'businessOpportunityType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="businessOpportunityType"
                    renderPattern="<name>"
                    renderTooltipPattern="[<acronym>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-businessopportunitytype'
        },
        {
            field: 'department',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="department"
                    renderPattern="<name>"
                    renderTooltipPattern="<name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-common-department'
        },
        {
            field: 'client',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="client"
                    renderPattern="<name>"
                    renderTooltipPattern="[<code>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT4}
                    icon={params?.row?.client?.partnershipType?.icon}
                    color={params?.row?.client?.partnershipType?.color}
                    {...params}
                />
            ),
            label: 'entity-crm-client'
        },
        {
            field: 'priority',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={<FormattedMessage id={`ui-comp-form-priority-${params.value || 'low'}`} />}>
                        <Box>
                            {params.value === 'low' && <LowPriorityTwoToneIcon fontSize="small" />}
                            {params.value === 'medium' && <DensityMediumTwoToneIcon fontSize="small" />}
                            {params.value === 'high' && <PriorityHighTwoToneIcon fontSize="small" />}
                        </Box>
                    </Tooltip>
                ),
            label: 'entity-workmgmt-businessopportunity-priority'
        },
        {
            field: 'expectedValue',
            type: 'number',
            description: '',
            align: 'right',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value !== null &&
                params?.value !== undefined && (
                    <Tooltip title={formatExpectedValueAsCurrency(params.value)}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 600
                            }}
                        >
                            {formatExpectedValueAsCurrency(params.value)}
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-workmgmt-businessopportunity-expectedvalue'
        },
        {
            field: 'responsible',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="employee"
                    renderPattern="<name> <lastName>"
                    renderTooltipPattern="[<code>] <name> <lastName>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-businessopportunity-responsible'
        },
        {
            field: 'tasks',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT4} {...params} />,
            label: 'entity-workmgmt-task-plural'
        },
        {
            field: 'taskFollowUps',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT5} {...params} />,
            label: 'entity-workmgmt-businessopportunity-taskfollowups'
        },
        {
            field: 'state',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT2} {...params} />,
            label: 'entity-workmgmt-businessopportunity-state'
        },
        {
            field: 'previousState',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT2} {...params} />,
            label: 'entity-workmgmt-businessopportunity-previousstate'
        },
        {
            field: 'execSettings',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            renderCell: (params) => <RenderExecSettingsGridCell {...params} />,
            label: 'entity-workmgmt-execsettings'
        },
        {
            field: 'notesCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            label: 'entity-workmgmt-note-plural'
        },
        {
            field: 'workOrderCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            label: 'entity-workmgmt-businessopportunity-workorders'
        },
        {
            field: 'cancellationReason',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Tooltip
                    title={
                        <div
                            style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1…') }}
                        />
                    }
                >
                    <div
                        style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1…') }}
                    />
                </Tooltip>
            ),
            label: 'entity-workmgmt-businessopportunity-cancellationreason'
        },
        {
            field: 'locked',
            type: 'boolean',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Box>
                    {params?.value ? (
                        <LockSharpIcon fontSize="small" color="error" />
                    ) : (
                        <LockOpenOutlinedIcon fontSize="small" color="info" />
                    )}
                </Box>
            ),
            label: 'traits-appentityislockable-locked'
        },
        {
            field: 'lockedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityislockable-lockedat'
        },
        {
            field: 'lockedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityislockable-lockedby'
        },
        {
            field: 'lockType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={<FormattedMessage id={`ui-comp-form-locktype-${params.value}`} />}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FormattedMessage id={`ui-comp-form-locktype-${params.value}`} />
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityislockable-locktype'
        },
        {
            field: 'lockTimeout',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityislockable-locktimeout'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                code: true,
                name: true,
                description: false,
                businessOpportunityType: false,
                department: true,
                client: true,
                priority: true,
                expectedValue: true,
                responsible: true,
                tasks: true,
                taskFollowUps: true,
                state: true,
                previousState: false,
                execSettings: true,
                notesCount: false,
                workOrderCount: false,
                cancellationReason: false,
                locked: true,
                lockedAt: false,
                lockedBy: false,
                lockType: false,
                lockTimeout: false,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'code',
                'name',
                'description',
                'businessOpportunityType',
                'department',
                'client',
                'priority',
                'expectedValue',
                'responsible',
                'tasks',
                'taskFollowUps',
                'state',
                'previousState',
                'execSettings',
                'notesCount',
                'workOrderCount',
                'cancellationReason',
                'locked',
                'lockedAt',
                'lockedBy',
                'lockType',
                'lockTimeout',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                code: { width: 100 },
                name: { width: 200 },
                description: { width: 100 },
                businessOpportunityType: { width: 150 },
                department: { width: 100 },
                client: { width: 100 },
                priority: { width: 100 },
                expectedValue: { width: 120 },
                responsible: { width: 100 },
                tasks: { width: 100 },
                taskFollowUps: { width: 100 },
                state: { width: 100 },
                previousState: { width: 100 },
                execSettings: { width: 100 },
                notesCount: { width: 100 },
                workOrderCount: { width: 100 },
                cancellationReason: { width: 100 },
                locked: { width: 100 },
                lockedAt: { width: 100 },
                lockedBy: { width: 100 },
                lockType: { width: 100 },
                lockTimeout: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 120 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        },
        toolbarFilters: { loadUserTasks: false, includeSafeDeleted: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'code', fieldType: 'simple' },
            label: 'traits-appentityhasautogencode-code'
        },
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'businessOpportunityTypeId', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunitytype',
            renderField: (params) => (
                <BusinessOpportunityTypeSelect key="filterBusinessOpportunity-businessOpportunityType" multiple {...params} />
            )
        },
        {
            filterType: 'reference',
            field: { fieldName: 'departmentId', fieldType: 'simple' },
            label: 'entity-common-department',
            renderField: (params) => <DepartmentSelect key="filterBusinessOpportunity-department" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'clientId', fieldType: 'simple' },
            label: 'entity-crm-client',
            renderField: (params) => <ClientSelect key="filterBusinessOpportunity-client" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'priority', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunity-priority',
            renderField: (params) => <PrioritySelect key="filterBusinessOpportunity-priority" multiple {...params} />
        },
        {
            filterType: 'number',
            field: { fieldName: 'expectedValue', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunity-expectedvalue'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'responsibleId', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunity-responsible',
            renderField: (params) => <EmployeeSelect key="filterBusinessOpportunity-responsible" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'state', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunity-state',
            renderField: (params) => <BusinessOpportunityStateSelect key="filterBusinessOpportunity-state" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'previousState', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunity-previousstate',
            renderField: (params) => <BusinessOpportunityStateSelect key="filterBusinessOpportunity-previousState" multiple {...params} />
        },
        {
            filterType: 'string',
            field: { fieldName: 'cancellationReason', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunity-cancellationreason'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'lockedBy', fieldType: 'simple', outputFieldName: 'lockedById' },
            label: 'traits-appentityislockable-lockedby',
            renderField: (params) => <UserSelect key="filterBusinessOpportunity-lockedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterBusinessOpportunity-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterBusinessOpportunity-updatedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => <UserSelect key="filterBusinessOpportunity-deletedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-businessopportunity',
        fields: [
            { fieldName: 'code', fieldType: 'simple' },
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'businessOpportunityTypeAcronym', fieldType: 'simple' },
            { fieldName: 'businessOpportunityTypeName', fieldType: 'simple' },
            { fieldName: 'clientCode', fieldType: 'simple' },
            { fieldName: 'clientName', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'code', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'businessOpportunityTypeId', fieldType: 'simple' },
        { fieldName: 'departmentId', fieldType: 'simple' },
        { fieldName: 'clientId', fieldType: 'simple' },
        { fieldName: 'priority', fieldType: 'simple' },
        { fieldName: 'expectedValue', fieldType: 'simple' },
        { fieldName: 'responsibleId', fieldType: 'simple' },
        { fieldName: 'state', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' },
        { fieldName: 'deletedAt', fieldType: 'simple' },
        { fieldName: 'deletedBy', fieldType: 'simple' }
    ]
};

export default initialState;
